import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Truck, Users, Music, Utensils, Camera, Tent } from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: Truck,
      title: 'Food Truck Catering',
      description: 'Vielfältige kulinarische Köstlichkeiten – regional, frisch & individuell'
    },
    {
      icon: Utensils,
      title: 'Cocktail & Getränkebars',
      description: 'VW T2 Bus mit Softdrinks, Bier oder hochwertigen Cocktails'
    },
    {
      icon: Camera,
      title: 'VW T2 Photobus',
      description: 'Mobile Fotobox mit Charme für unvergessliche Erinnerungen'
    },
    {
      icon: Music,
      title: 'Bühnenprogramme',
      description: 'DJs & Künstler (regional & national) für Ihr Event'
    },
    {
      icon: Tent,
      title: 'Event-Equipment',
      description: 'Zelte, Loungemöbel, Licht & Tontechnik'
    },
    {
      icon: Users,
      title: 'Servicepersonal',
      description: 'Professionelles Team für Auf- und Abbau sowie Betreuung'
    }
  ];

  const eventTypes = [
    {
      title: 'Hochzeiten',
      description: 'Unvergessliche kulinarische Erlebnisse für Ihren besonderen Tag',
      image: 'https://customer-assets.emergentagent.com/job_event-truck/artifacts/ne9jnzqj_AdobeStock_283732318.jpeg'
    },
    {
      title: 'Firmenfeiern',
      description: 'Moderne Event-Lösungen für Ihr Unternehmen',
      image: 'https://customer-assets.emergentagent.com/job_event-truck/artifacts/w6xkdfit_156.JPG'
    },
    {
      title: 'Festivals',
      description: 'Besondere Stände für Ihr Festivalgelände',
      image: 'https://customer-assets.emergentagent.com/job_event-truck/artifacts/v9sbmmq7_IMG_4501.JPG'
    },
    {
      title: 'Geburtstage',
      description: 'Ob groß oder klein – wir machen Ihren Tag besonders',
      image: 'https://customer-assets.emergentagent.com/job_event-truck/artifacts/kr7obt23_IMG_0376%20copy.JPG'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=1920&h=1080&fit=crop)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            Willkommen bei
            <br />
            <span className="text-red-600">UM ULM EVENTS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            Eventcatering mal anders!
          </p>
          <p className="text-lg mb-10 max-w-2xl mx-auto">
            Professionelles Eventcatering & Food Truck Service – Während Sie Ihr Event genießen,
            sorgen wir zuverlässig für Ihre Gäste.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Jetzt Angebot anfordern
              </Button>
            </Link>
            <Link to="/events">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg">
                Unsere Events
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Über <span className="text-red-600">Um Ulm Events</span>
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Wir sind eine erfahrene Eventagentur aus Ulm mit dem Fokus auf Food Truck Catering
                und mobile Eventlösungen.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Seit 2017 betreuen wir erfolgreich Events jeder Art: Firmenfeiern, Geburtstage,
                Weihnachtsfeiern, Streetfood-Märkte, Neueröffnungen, Festivals und Stadtfeste –
                professionell, kreativ und flexibel.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Von 50 bis 3.000 Gästen – wir machen jede Veranstaltung zu einem unvergesslichen Erlebnis.
              </p>
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600">8+</div>
                  <div className="text-sm text-gray-600">Jahre Erfahrung</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600">650+</div>
                  <div className="text-sm text-gray-600">Events</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600">3000</div>
                  <div className="text-sm text-gray-600">Max. Gäste</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_event-truck/artifacts/tdrlon35_AdobeStock_257344434.jpeg"
                alt="Um Ulm Events Festival"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Unser <span className="text-red-600">Angebot</span>
            </h2>
            <p className="text-xl text-gray-600">
              Alles für Ihr Event aus einer Hand
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white"
                >
                  <CardContent className="p-6">
                    <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Event Types Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Unsere <span className="text-red-600">Events</span>
            </h2>
            <p className="text-xl text-gray-600">
              Für jeden Anlass die perfekte Lösung
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventTypes.map((event, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 h-80"
              >
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{event.title}</h3>
                  <p className="text-sm opacity-90">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/events">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8">
                Alle Events ansehen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 px-4 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Wir sind in Ihrer Nähe
          </h2>
          <p className="text-xl mb-8">
            Aktiv in Ulm, Neu-Ulm, Blaustein, Senden, Ehingen, Laupheim, Illertissen, Günzburg & Umgebung
          </p>
          <Link to="/contact">
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-8">
              Jetzt Kontakt aufnehmen
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Bereit für Ihr unvergessliches Event?
          </h2>
          <p className="text-xl mb-10 text-gray-300">
            Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-12 py-6 text-lg">
              Unverbindliches Angebot anfordern
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;